import { createListenerMiddleware } from '@reduxjs/toolkit'
import { LOCATION_CHANGE } from 'lib/routing/appRouting'
import {
  SET_BYPASS_REGION_EXCLUSION_OFFER_IDS,
  SET_BYPASS_REGION_SETTING_ID,
} from 'actions/actionConstants'
import { getRegionChannelSettings, REGION_CHANNEL_SETTING_TYPE } from 'api/promo'
import { getCookies, setCookie } from 'lib/web/cookieUtils'

// Client-side fallback for SPA navigations where SSR didn't seed the offer region bypass (MLOS).
// Mirrors fetchRegionChannelSettings on the server: the cookie marks that this user's region + UTM
// matched an enabled bypass rule, so the bypass stays on for the rest of the visit without
// re-resolving.
const MATCHED_COOKIE_KEY = 'bypass_region_setting_id'
const EXCLUSION_IDS_COOKIE_KEY = 'bypass_region_exclusion_ids'
const BYPASS_COOKIE_TTL_SECONDS = 7 * 24 * 60 * 60 // 7 days

const regionChannelSettingsMiddleware = createListenerMiddleware()

// A matched setting id turns the bypass on for this visit.
function applyMatchedBypassSetting(
  listenerApi: { dispatch: (action: unknown) => unknown },
  matchedSettingId: string,
) {
  listenerApi.dispatch({ type: SET_BYPASS_REGION_SETTING_ID, settingId: matchedSettingId })
}

regionChannelSettingsMiddleware.startListening({
  type: LOCATION_CHANGE,
  effect: async(_action, listenerApi) => {
    listenerApi.unsubscribe()

    const state = listenerApi.getState() as App.State
    if (state.marketing.bypassRegionSettingId) return

    // 1. Settle from the cookie where possible, so we only hit svc-promo when the bypass is unresolved.
    const cookies = getCookies()
    const savedBypassSettingId = cookies[MATCHED_COOKIE_KEY]
    if (savedBypassSettingId) {
      const savedIds = cookies[EXCLUSION_IDS_COOKIE_KEY]
      if (savedIds) {
        listenerApi.dispatch({ type: SET_BYPASS_REGION_EXCLUSION_OFFER_IDS, offerIds: savedIds.split(',') })
      }
      applyMatchedBypassSetting(listenerApi, savedBypassSettingId)
      return
    }

    const { source, medium, campaign, term, content } = state.utm
    const region = state.geo.currentRegionCode

    try {
      const settings = await getRegionChannelSettings({
        region: region || undefined,
        source: source || undefined,
        medium: medium || undefined,
        campaign: campaign || undefined,
        term: term || undefined,
        content: content || undefined,
      })
      const bypass = settings[REGION_CHANNEL_SETTING_TYPE.offerRegionFilterBypass]

      // Offer exclusions are global and experiment-independent — always applied when present.
      const excludedOfferIds = bypass?.excluded_offer_ids ?? []
      if (excludedOfferIds.length > 0) {
        setCookie(EXCLUSION_IDS_COOKIE_KEY, excludedOfferIds.join(','), BYPASS_COOKIE_TTL_SECONDS, '/', 'Lax', document.domain)
        listenerApi.dispatch({ type: SET_BYPASS_REGION_EXCLUSION_OFFER_IDS, offerIds: excludedOfferIds })
      }

      // A disabled winning rule means no bypass, same as no rule at all.
      if (bypass?.enabled && bypass.setting_id) {
        setCookie(MATCHED_COOKIE_KEY, bypass.setting_id, BYPASS_COOKIE_TTL_SECONDS, '/', 'Lax', document.domain)
        applyMatchedBypassSetting(listenerApi, bypass.setting_id)
      }
    } catch {
      // non-critical — the bypass stays unset
    }
  },
})

export default regionChannelSettingsMiddleware
